import React, { useCallback, useEffect, useState } from "react"

import "./MultiSelectAnswers.scss"
import ContainedImg from "@Quiz/Ui/ContainedImg"
import { useQuiz } from "@Quiz/context"
import Button from "@ui/Button"
import i18nInstance from "@helper/i18nInstance"
import onEnterOrSpace from "@helper/onEnterOrSpace"
import _ from "lodash"

interface MultiSelectAnswersProps {
  conf: Quiz.Api.MultiSelect.QuestionConfig
  tryAgain?: Quiz.Api.MultiSelect.TryAgainInfo
}

interface MultiSelectState {
  selectedVals: number[]
  markEvalErrors: boolean
}

const useMultiSelectState = (tryAgain?: Quiz.Api.MultiSelect.TryAgainInfo) => {
  const [ state, setState ] = useState<MultiSelectState>({ selectedVals: [], markEvalErrors: false })
  const { busy, quiz } = useQuiz()

  const onSelectedClick = useCallback((val: number) => { // toggle val-selection, clear markEvalErrors
    setState((prev) => ({
      selectedVals: prev.selectedVals.indexOf(val) > -1 ? prev.selectedVals.filter((v) => v !== val) : [...prev.selectedVals, val],
      markEvalErrors: false, // always clear on selection change
    }))
  }, [])

  const hasEval = !!quiz?.question?.eval

  useEffect(() => { // set markEvalErrors when eval but not (no longer) busy
    if (!busy && hasEval && tryAgain) {
      setState({
        selectedVals: tryAgain.previous_picked_options,
        markEvalErrors: true
      })
    }
  }, [tryAgain, hasEval, busy])

  return { state, onSelectedClick }
}

export const MultiSelectResultInfo: React.FC<{ question: Quiz.Api.MultiSelectQuestion}> = ({ question }) => {  
  const resultInfo = question.eval?.result?.result_info

  if (!resultInfo) { return null }
  return resultInfo.correct_options.length === 0
    ? <div data-testid="quiz-multi-select-correct-options" className="neo__multiSelect__resultInfo__noCorrectOptions">{i18nInstance.t("quiz.question.multi_select.no_correct_options")}</div>
    : <div data-testid="quiz-multi-select-correct-options" className="neo__multiSelect__resultInfo__correctList">
        <div className="neo__multiSelect__resultInfo__correctList__title">{i18nInstance.t("quiz.question.multi_select.reveal_title")}</div>
        {resultInfo.correct_options.map((val, index) => {
          const option = _.find(question.config!.options, (o) => o.val === val)
          if (!option) { return null }
          return <div key={index} className="neo__multiSelect__resultInfo__correctList__item">
            <SelectButtonOptionImageAndOrText option={option} />
            <div className="neo__multiSelect__resultInfo__correctList__item__correctMarker" />
          </div>
        })}
      </div>
}

const MultiSelectAnswers: React.FC<MultiSelectAnswersProps> = ({ conf, tryAgain }) => {
  const { state, onSelectedClick } = useMultiSelectState(tryAgain)
  const { quiz, actions } = useQuiz()
  const hadFinalEval = !!quiz?.question?.eval && !tryAgain

  return <div className="neo__multiSelect__holder">
    <div className="neo__multiSelect__answers">
      <div className="neo__multiSelect__selectionInfo">{i18nInstance.t("quiz.question.multi_select.selection_info")}</div>
      {conf.options.map((opt, index) => {
        const selected = state.selectedVals.indexOf(opt.val) > -1
        const marking: SelectButtonMarking | undefined = hadFinalEval
          ? undefined
          : state.markEvalErrors
            ? (selected ? "errorSelected" : "errorUnselected")
            : (selected ? "activeSelected" : "activeUnselected")

        return <SelectButton marking={marking} key={index} option={opt} onClick={() => onSelectedClick(opt.val)}  />
      })}
    </div>
    <div className="neo__multiSelect__submitButtonHolder">
      <Button
        onClick={() => actions.answerQuestion({ type: "multi_select", picked_options: state.selectedVals })}>
        {i18nInstance.t("quiz.question.multi_select.btn_send_answer")}
      </Button>
    </div>
  </div>
}

const SelectButtonOptionImageAndOrText: React.FC<{ option: Quiz.Api.AnswerOptionBase }> = ({ option }) => {
  return <div className={"neo__multiSelect__button__imageAndTextHolder" + (option.text && !option.image ? " neo__multiSelect__button__imageAndTextHolder--onlyText" : "")}>
    {option.image &&
      <span className="neo__multiSelect__button__image">
        <ContainedImg className="neo__multiSelect__button__image__img" src={option.image["1x"]} displaySize={option.image_display_size} />
        {option.image_credit && <div className="neo__multiSelect__button__image__imgCredit">{option.image_credit}</div>}
      </span>}
    {option.text &&
      <span className="neo__multiSelect__button__text" dangerouslySetInnerHTML={{__html: option.text}} />}
  </div>
}

type SelectButtonMarking =
  | "activeSelected"
  | "activeUnselected"
  | "errorSelected"
  | "errorUnselected"
  | "evalCorrect"

const SelectButton: React.FC<{marking?: SelectButtonMarking, onClick(): void, option: Quiz.Api.AnswerOptionBase}> = ({option, marking, onClick}) => {
  const className = [
    "neo__multiSelect__button",
    marking ? `neo__multiSelect__button--${marking}` : "",
  ].join(" ")

  return <div className={className} role="button" tabIndex={0} onClick={onClick} onKeyDown={onEnterOrSpace(onClick)}>
    <SelectButtonOptionImageAndOrText option={option} />
    <div className="neo__multiSelect__button__checkbox" />
  </div>
}

export default MultiSelectAnswers
